import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { API_URL } from '../utils/api';

const MonthlyRecap = () => {
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedClassroom, setSelectedClassroom] = useState('all');
  const [personTab, setPersonTab] = useState('student');
  const isTeacherTab = personTab === 'teacher';

  const months = [
    { value: 1, label: 'Januari' },
    { value: 2, label: 'Februari' },
    { value: 3, label: 'Maret' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mei' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'Agustus' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Desember' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const grades = [1, 2, 3, 4, 5, 6];
  const classrooms = ['A', 'B', 'C'];

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear, selectedGrade, selectedClassroom, personTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/reports/monthly-recap`, {
        params: {
          month: selectedMonth,
          year: selectedYear,
          grade: isTeacherTab ? 'all' : selectedGrade,
          classroom: isTeacherTab ? 'all' : selectedClassroom,
          person_type: personTab,
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching monthly recap:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const isSunday = (day) => new Date(selectedYear, selectedMonth - 1, day).getDay() === 0;

  const sundayHeaderSx = {
    bgcolor: '#7c3aed !important',
    color: 'white',
    border: '1px solid #5b21b6',
  };

  const sundayBodySx = {
    bgcolor: '#ede9fe',
    color: '#5b21b6',
    fontWeight: 600,
  };

  const getStatusCode = (status) => {
    switch (status) {
      case 'present': return 'H';
      case 'late': return 'T';
      case 'permission': return 'I';
      case 'sick': return 'S';
      case 'absent': return 'A';
      default: return '-';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return '#d1fae5'; // green-100
      case 'late': return '#fef3c7'; // yellow-100
      case 'permission': return '#dbeafe'; // blue-100
      case 'sick': return '#e0e7ff'; // indigo-100
      case 'absent': return '#fee2e2'; // red-100
      default: return 'transparent';
    }
  };

  const getEffectiveCheckInStatus = (student, day) => {
    const attendance = student.attendance[day];
    if (attendance?.checkInStatus) return attendance.checkInStatus;

    // Check if date is valid for Alpha
    const date = new Date(selectedYear, selectedMonth - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // If future date, return empty
    if (date > today) return null;

    // If Sunday, return empty (or holiday)
    if (date.getDay() === 0) return null;

    // If past/today and no status -> Alpha
    return 'absent';
  };

  const hasEffectiveCheckOut = (student, day) => {
    const attendance = student.attendance[day];
    if (attendance?.hasCheckOut) return true;

    const date = new Date(selectedYear, selectedMonth - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date > today) return null;
    if (date.getDay() === 0) return null;
    return false;
  };

  const calculateStats = (student) => {
    let h = 0, s = 0, i = 0, a = 0, t = 0, co = 0;
    
    daysArray.forEach(day => {
      const checkInStatus = getEffectiveCheckInStatus(student, day);
      const hasCheckOut = hasEffectiveCheckOut(student, day);
      if (checkInStatus === 'present') h++;
      else if (checkInStatus === 'sick') s++;
      else if (checkInStatus === 'permission') i++;
      else if (checkInStatus === 'absent') a++;
      else if (checkInStatus === 'late') t++;
      if (hasCheckOut) co++;
    });
    
    return { h, s, i, a, t, co };
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape
    
    // Title
    doc.setFontSize(16);
    const recapLabel = isTeacherTab ? 'GURU' : 'SISWA';
    doc.text(`REKAP ABSENSI ${recapLabel} BULAN ${months.find(m => m.value === selectedMonth)?.label.toUpperCase()} ${selectedYear}`, 14, 20);
    doc.setFontSize(12);
    if (!isTeacherTab) {
      doc.text(`Kelas: ${selectedGrade === 'all' ? 'Semua' : selectedGrade}`, 14, 28);
    }

    // Table
    const tableColumn = [
      'No', isTeacherTab ? 'Nama Guru' : 'Nama Siswa',
      ...daysArray.flatMap(d => [d.toString() + ' M', d.toString() + ' P']), 
      'H', 'S', 'I', 'A', 'P'
    ];

    const tableRows = students.map((student, index) => {
      const stats = calculateStats(student);
      const row = [
        index + 1,
        student.name,
        ...daysArray.flatMap(day => {
          if (isSunday(day)) return ['Mg', 'Mg'];
          const checkInStatus = getEffectiveCheckInStatus(student, day);
          const hasCheckOut = hasEffectiveCheckOut(student, day);
          const checkInCode = checkInStatus ? getStatusCode(checkInStatus) : '-';
          const checkOutCode = hasCheckOut ? '✓' : (hasCheckOut === null ? '' : '-');
          return [checkInCode, checkOutCode];
        }),
        stats.h + stats.t, // Total Hadir (termasuk terlambat)
        stats.s,
        stats.i,
        stats.a,
        stats.co
      ];
      return row;
    });

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 35,
      styles: { fontSize: 7, cellPadding: 1 },
      columnStyles: {
        0: { cellWidth: 8 },
        1: { cellWidth: 40 },
        // Dynamic width for date columns
      },
      theme: 'grid'
    });

    // Signature
    const finalY = doc.lastAutoTable.finalY + 20;
    doc.text(`.................., ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, 200, finalY);
    doc.text('Wali Kelas,', 200, finalY + 7);
    doc.text('(_______________________)', 200, finalY + 30);

    doc.save(`rekap_absensi_${selectedMonth}_${selectedYear}.pdf`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }} className="no-print">
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
            Rekap Bulanan {isTeacherTab ? 'Guru' : 'Siswa'}
          </Typography>
          <ToggleButtonGroup
            value={personTab}
            exclusive
            onChange={(_, v) => v && setPersonTab(v)}
            size="small"
            sx={{ mt: 1 }}
          >
            <ToggleButton value="student">Siswa</ToggleButton>
            <ToggleButton value="teacher">Guru</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<PrintIcon />} 
            onClick={handlePrint}
            sx={{ mr: 1 }}
          >
            Cetak
          </Button>
          <Button 
            variant="contained" 
            startIcon={<DownloadIcon />} 
            onClick={handleExportPDF}
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }} className="no-print">
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Bulan</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Bulan"
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {months.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tahun</InputLabel>
                <Select
                  value={selectedYear}
                  label="Tahun"
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {!isTeacherTab && (
              <>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Kelas</InputLabel>
                    <Select
                      value={selectedGrade}
                      label="Kelas"
                      onChange={(e) => setSelectedGrade(e.target.value)}
                    >
                      <MenuItem value="all">Semua</MenuItem>
                      {grades.map((g) => (
                        <MenuItem key={g} value={g}>Kelas {g}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Rombel</InputLabel>
                    <Select
                      value={selectedClassroom}
                      label="Rombel"
                      onChange={(e) => setSelectedClassroom(e.target.value)}
                    >
                      <MenuItem value="all">Semua</MenuItem>
                      {classrooms.map((c) => (
                        <MenuItem key={c} value={c}>Rombel {c}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            <Grid item xs={12} md={2}>
              <Button 
                variant="contained" 
                fullWidth 
                onClick={fetchData}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Tampilkan'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Report View */}
      <Paper sx={{ p: 2, overflowX: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            REKAP ABSENSI {isTeacherTab ? 'GURU' : 'SISWA'}
          </Typography>
          <Typography variant="subtitle1">
            Bulan: {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
          </Typography>
          {!isTeacherTab && (selectedGrade !== 'all' || selectedClassroom !== 'all') && (
            <Typography variant="subtitle2">
              Filter: {selectedGrade === 'all' ? 'Semua Kelas' : `Kelas ${selectedGrade}`}
              {selectedClassroom === 'all' ? '' : ` • Rombel ${selectedClassroom}`}
            </Typography>
          )}
        </Box>

        <TableContainer>
          <Table size="small" sx={{ minWidth: 1200, border: '1px solid #e2e8f0' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1e293b' }}>
                <TableCell rowSpan={2} sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center', width: 40 }}>No</TableCell>
                <TableCell rowSpan={2} sx={{ color: 'white', border: '1px solid #475569', minWidth: 200 }}>
                  {isTeacherTab ? 'Nama Guru' : 'Nama Siswa'}
                </TableCell>
                <TableCell colSpan={daysInMonth * 2} sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center' }}>
                  Tanggal ({months.find(m => m.value === selectedMonth)?.label})
                </TableCell>
                <TableCell colSpan={5} sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center' }}>Total</TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: '#334155' }}>
                {daysArray.map(day => (
                  <React.Fragment key={day}>
                    <TableCell
                      sx={{
                        color: 'white',
                        border: '1px solid #475569',
                        textAlign: 'center',
                        minWidth: 28,
                        padding: '2px 4px',
                        lineHeight: 1.2,
                        ...(isSunday(day) ? sundayHeaderSx : {}),
                      }}
                    >
                      {isSunday(day) ? (
                        <Tooltip title="Hari Minggu (libur)" arrow>
                          <Box component="span" sx={{ display: 'block', cursor: 'help' }}>
                            <Box component="span" sx={{ display: 'block', fontWeight: 700 }}>{day}</Box>
                            <Box component="span" sx={{ display: 'block', fontSize: '0.6rem', letterSpacing: 0.5 }}>Mg</Box>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box>
                          <Box sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>{day}</Box>
                          <Box sx={{ fontSize: '0.6rem' }}>Masuk</Box>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        border: '1px solid #475569',
                        textAlign: 'center',
                        minWidth: 28,
                        padding: '2px 4px',
                        lineHeight: 1.2,
                        ...(isSunday(day) ? sundayHeaderSx : {}),
                      }}
                    >
                      {isSunday(day) ? (
                        <Box sx={{ fontSize: '0.6rem' }}>Mg</Box>
                      ) : (
                        <Box sx={{ fontSize: '0.6rem' }}>Pulang</Box>
                      )}
                    </TableCell>
                  </React.Fragment>
                ))}
                <TableCell sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center', bgcolor: '#10b981', width: 30 }}>H</TableCell>
                <TableCell sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center', bgcolor: '#3b82f6', width: 30 }}>S</TableCell>
                <TableCell sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center', bgcolor: '#f59e0b', width: 30 }}>I</TableCell>
                <TableCell sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center', bgcolor: '#ef4444', width: 30 }}>A</TableCell>
                <TableCell sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center', bgcolor: '#06b6d4', width: 30 }}>P</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={daysInMonth * 2 + 7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : students.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={daysInMonth * 2 + 7} align="center" sx={{ py: 4 }}>
                    Tidak ada data {isTeacherTab ? 'guru' : 'siswa'}
                  </TableCell>
                </TableRow>
              ) : (
                students.map((student, index) => {
                  const stats = calculateStats(student);
                  return (
                    <TableRow key={student.id} hover>
                      <TableCell align="center" sx={{ border: '1px solid #e2e8f0' }}>{index + 1}</TableCell>
                      <TableCell sx={{ border: '1px solid #e2e8f0', fontWeight: 500 }}>{student.name}</TableCell>
                      {daysArray.map(day => {
                        const sunday = isSunday(day);
                        const checkInStatus = getEffectiveCheckInStatus(student, day);
                        const hasCheckOut = hasEffectiveCheckOut(student, day);
                        const checkInCode = sunday ? 'Mg' : (checkInStatus ? getStatusCode(checkInStatus) : '-');
                        const checkInColor = sunday ? '#ede9fe' : (checkInStatus ? getStatusColor(checkInStatus) : 'transparent');
                        const checkOutCode = sunday ? 'Mg' : (hasCheckOut ? '✓' : (hasCheckOut === null ? '' : '-'));
                        const checkOutColor = sunday ? '#ede9fe' : (hasCheckOut ? '#d1fae5' : 'transparent');
                        return (
                          <React.Fragment key={day}>
                            <TableCell
                              align="center"
                              sx={{
                                border: '1px solid #e2e8f0',
                                bgcolor: checkInColor,
                                padding: '2px',
                                fontSize: '0.75rem',
                                ...(sunday ? sundayBodySx : {}),
                              }}
                            >
                              {sunday || checkInCode !== '-' ? checkInCode : ''}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                border: '1px solid #e2e8f0',
                                bgcolor: checkOutColor,
                                padding: '2px',
                                fontSize: '0.75rem',
                                ...(sunday ? sundayBodySx : {}),
                              }}
                            >
                              {sunday || checkOutCode !== '-' ? checkOutCode : ''}
                            </TableCell>
                          </React.Fragment>
                        );
                      })}
                      <TableCell align="center" sx={{ border: '1px solid #e2e8f0', fontWeight: 'bold' }}>{stats.h + stats.t}</TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #e2e8f0', fontWeight: 'bold' }}>{stats.s}</TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #e2e8f0', fontWeight: 'bold' }}>{stats.i}</TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #e2e8f0', fontWeight: 'bold', color: 'error.main' }}>{stats.a}</TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #e2e8f0', fontWeight: 'bold' }}>{stats.co}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            fontSize: '0.8rem',
            color: '#475569',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 28, height: 20, bgcolor: '#7c3aed', borderRadius: 0.5, border: '1px solid #5b21b6' }} />
            <Typography variant="caption">Kolom ungu = tanggal hari Minggu</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                px: 0.75,
                py: 0.25,
                bgcolor: '#ede9fe',
                color: '#5b21b6',
                fontWeight: 700,
                fontSize: '0.7rem',
                borderRadius: 0.5,
                border: '1px solid #c4b5fd',
              }}
            >
              Mg
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                px: 0.75,
                py: 0.25,
                bgcolor: '#dbeafe',
                color: '#1d4ed8',
                fontWeight: 700,
                fontSize: '0.7rem',
                borderRadius: 0.5,
                border: '1px solid #93c5fd',
              }}
            >
              I
            </Box>
            <Typography variant="caption">= Izin</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                px: 0.75,
                py: 0.25,
                bgcolor: '#e0e7ff',
                color: '#4338ca',
                fontWeight: 700,
                fontSize: '0.7rem',
                borderRadius: 0.5,
                border: '1px solid #a5b4fc',
              }}
            >
              S
            </Box>
            <Typography variant="caption">= Sakit</Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ textAlign: 'center', minWidth: 200 }}>
            <Typography variant="body2">
              .................., {new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>Wali Kelas,</Typography>
            <Box sx={{ height: 60 }} />
            <Typography variant="body2" sx={{ borderTop: '1px solid black', pt: 1 }}>
              (_______________________)
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MonthlyRecap;
